import { useRef, useState, type FormEvent } from "react";
import { ArrowUpRight } from "lucide-react";
import { GOOGLE_ADS_LEAD_SEND_TO, hasAdsConsent } from "@/lib/adsConsent";

const SERVICE_OPTIONS = [
  "SEO (General)",
  "Local SEO",
  "E-Commerce SEO",
  "Technical SEO",
  "Content Optimisation",
  "Analytics & Reporting",
  "Web Design",
  "WordPress Websites",
  "WordPress Development",
  "Shopify Websites",
  "CMS Development",
  "Landing Page Design",
  "Web Hosting",
  "WordPress Hosting & Maintenance",
  "Website Management",
  "Ongoing Site Support",
  "Custom Project / Something Else",
];

const RECIPIENT = "info@phil-carr.co.uk";
const RECAPTCHA_SITE_KEY = "6LeSl0QsAAAAAIYofsEXCEPBdDgo4vg9TQ2TPHrF";
const RECAPTCHA_SCRIPT_ID = "google-recaptcha-v3";
const RECAPTCHA_SCRIPT_URL = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
const FORM_ENDPOINT = "/includes/process-form.php";

declare global {
  interface Window {
    grecaptcha?: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

function sanitize(value: string, max: number): string {
  return value.trim().slice(0, max);
}

async function loadRecaptcha(): Promise<void> {
  if (window.grecaptcha) return;

  const existingScript = document.getElementById(RECAPTCHA_SCRIPT_ID) as HTMLScriptElement | null;

  await new Promise<void>((resolve, reject) => {
    const script = existingScript ?? document.createElement("script");

    const handleLoad = () => {
      cleanup();
      if (window.grecaptcha) {
        resolve();
      } else {
        reject(new Error("Recaptcha requires verification."));
      }
    };
    const handleError = () => {
      cleanup();
      script.remove();
      reject(new Error("Recaptcha requires verification."));
    };
    const cleanup = () => {
      script.removeEventListener("load", handleLoad);
      script.removeEventListener("error", handleError);
    };

    script.addEventListener("load", handleLoad, { once: true });
    script.addEventListener("error", handleError, { once: true });

    if (!existingScript) {
      script.id = RECAPTCHA_SCRIPT_ID;
      script.src = RECAPTCHA_SCRIPT_URL;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  });
}

export function ContactForm({ pageContext }: { pageContext?: string }) {
  const [submitted, setSubmitted] = useState(false);
  const [status, setStatus] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const conversionSent = useRef(false);

  async function getRecaptchaToken(): Promise<string> {
    await loadRecaptcha();

    return new Promise((resolve, reject) => {
      window.grecaptcha?.ready(() => {
        window.grecaptcha
          ?.execute(RECAPTCHA_SITE_KEY, { action: "contact" })
          .then(resolve)
          .catch(() => reject(new Error("Recaptcha requires verification.")));
      });
    });
  }

  function appendAttribution(data: FormData) {
    const url = new URL(window.location.href);
    data.set("conversion_page", window.location.pathname);
    data.set("referrer", document.referrer);
    data.set("page_group", pageContext ?? document.title);

    for (const key of [
      "utm_source",
      "utm_medium",
      "utm_campaign",
      "utm_term",
      "utm_content",
      "gclid",
      "msclkid",
    ]) {
      const value = url.searchParams.get(key);
      if (value) data.set(key, value);
    }

    try {
      const landingPage =
        window.localStorage.getItem("philCarrLandingPage") ?? window.location.pathname;
      const firstTouchAt =
        window.localStorage.getItem("philCarrFirstTouchAt") ?? new Date().toISOString();
      window.localStorage.setItem("philCarrLandingPage", landingPage);
      window.localStorage.setItem("philCarrFirstTouchAt", firstTouchAt);
      data.set("landing_page", landingPage);
      data.set("first_touch_at", firstTouchAt);
      data.set("attribution_storage", "localStorage");
    } catch {
      data.set("landing_page", window.location.pathname);
      data.set("first_touch_at", new Date().toISOString());
      data.set("attribution_storage", "unavailable");
    }
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    const fname = sanitize(String(data.get("fname") ?? ""), 80);
    const lname = sanitize(String(data.get("lname") ?? ""), 80);
    const email = sanitize(String(data.get("email") ?? ""), 255);
    const service = sanitize(String(data.get("service") ?? ""), 80);
    const message = sanitize(String(data.get("message") ?? ""), 2000);

    if (!fname || !lname || !email || !service || !message) {
      setStatus("Please complete all required fields.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus("Please enter a valid email address.");
      return;
    }
    if (message.trim().length < 20) {
      setStatus("Please provide a little more detail in your message.");
      return;
    }

    setIsSubmitting(true);
    setStatus("Sending your enquiry...");

    try {
      data.set("fname", fname);
      data.set("lname", lname);
      data.set("email", email);
      data.set("service", service);
      data.set("message", message);
      appendAttribution(data);
      data.set("g-recaptcha-response", await getRecaptchaToken());

      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        body: data,
        headers: { Accept: "application/json" },
      });
      const result = (await response.json().catch(() => null)) as {
        status?: string;
        message?: string;
      } | null;

      if (!response.ok || result?.status !== "success") {
        throw new Error(result?.message || "Something went wrong. Please try again.");
      }

      if (hasAdsConsent() && !conversionSent.current) {
        conversionSent.current = true;
        try {
          window.gtag?.("event", "conversion", {
            send_to: GOOGLE_ADS_LEAD_SEND_TO,
          });
        } catch {
          // Analytics must not turn a delivered enquiry into a form error.
        }
      }
      form.reset();
      setSubmitted(true);
      setStatus("");
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div className="demo-card demo-card-accent demo-card-accent-fade p-8 text-center">
        <h3 className="font-display text-2xl">Thanks — your enquiry has been sent</h3>
        <p className="mt-3 text-muted-foreground">
          I&apos;ll be in touch shortly. If it&apos;s urgent, call{" "}
          <a href="tel:+441226697325" className="text-primary underline-offset-4 hover:underline">
            01226 697 325
          </a>
          .
        </p>
        <button
          type="button"
          onClick={() => {
            conversionSent.current = false;
            setSubmitted(false);
          }}
          className="demo-button-secondary mt-6 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold"
        >
          Send another enquiry
        </button>
      </div>
    );
  }

  return (
    <form
      id="contactForm"
      action={FORM_ENDPOINT}
      method="POST"
      onSubmit={handleSubmit}
      className="demo-card demo-card-accent demo-card-accent-fade demo-form-card grid gap-5 p-6 sm:p-8"
      noValidate
    >
      <div className="grid gap-5 sm:grid-cols-2">
        <label className="grid gap-2 text-sm">
          <span className="font-medium text-foreground">
            First Name <span className="text-primary">*</span>
          </span>
          <input
            type="text"
            name="fname"
            required
            maxLength={80}
            autoComplete="given-name"
            className="demo-form-control"
          />
        </label>
        <label className="grid gap-2 text-sm">
          <span className="font-medium text-foreground">
            Last Name <span className="text-primary">*</span>
          </span>
          <input
            type="text"
            name="lname"
            required
            maxLength={80}
            autoComplete="family-name"
            className="demo-form-control"
          />
        </label>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <label className="grid gap-2 text-sm">
          <span className="font-medium text-foreground">
            Email <span className="text-primary">*</span>
          </span>
          <input
            type="email"
            name="email"
            required
            maxLength={255}
            autoComplete="email"
            className="demo-form-control"
          />
        </label>
        <label className="grid gap-2 text-sm">
          <span className="font-medium text-foreground">Phone</span>
          <input
            type="tel"
            name="phone"
            maxLength={30}
            autoComplete="tel"
            className="demo-form-control"
          />
        </label>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <label className="grid gap-2 text-sm">
          <span className="font-medium text-foreground">
            Service <span className="text-primary">*</span>
          </span>
          <select name="service" defaultValue="" required className="demo-form-control">
            <option value="">Select a service</option>
            {SERVICE_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label className="grid gap-2 text-sm">
        <span className="font-medium text-foreground">
          Message <span className="text-primary">*</span>
        </span>
        <textarea
          name="message"
          required
          maxLength={2000}
          rows={6}
          className="demo-form-control demo-form-textarea resize-y"
        />
      </label>
      <input type="hidden" name="landing_page" value="" />
      <input type="hidden" name="conversion_page" value="" />
      <input type="hidden" name="first_touch_at" value="" />
      <input type="hidden" name="attribution_model" value="" />
      <input type="hidden" name="attribution_storage" value="" />
      <input type="hidden" name="attribution_age_minutes" value="" />
      <input type="hidden" name="attribution_age_bucket" value="" />
      <input type="hidden" name="attribution_over_24h" value="" />
      <input type="hidden" name="page_group" value="" />
      <input type="hidden" name="referrer" value="" />
      <input type="hidden" name="utm_source" value="" />
      <input type="hidden" name="utm_medium" value="" />
      <input type="hidden" name="utm_campaign" value="" />
      <input type="hidden" name="utm_term" value="" />
      <input type="hidden" name="utm_content" value="" />
      <input type="hidden" name="gclid" value="" />
      <input type="hidden" name="msclkid" value="" />
      <div className="absolute -left-[5000px]" aria-hidden="true">
        <input
          type="text"
          name="company"
          tabIndex={-1}
          autoComplete="new-password"
          inputMode="none"
        />
      </div>
      <button
        type="submit"
        disabled={isSubmitting}
        className="group demo-form-submit demo-button-primary demo-arrow-btn inline-flex w-fit items-center gap-3 rounded-full py-0 pl-7 pr-0 text-sm font-bold transition-transform hover:-translate-y-0.5"
      >
        {isSubmitting ? "Sending..." : "Submit Your Enquiry"}
        <span className="demo-cta-arrow-icon grid h-12 w-12 place-items-center rounded-full text-primary-foreground transition-transform group-hover:rotate-45">
          <ArrowUpRight className="h-4 w-4" />
        </span>
      </button>
      {status && (
        <p className="text-sm text-muted-foreground" role="status">
          {status}
        </p>
      )}
      <p className="text-xs text-muted-foreground">
        Protected by reCAPTCHA. Prefer a direct email?{" "}
        <a href={`mailto:${RECIPIENT}`} className="text-primary underline-offset-4 hover:underline">
          {RECIPIENT}
        </a>
      </p>
    </form>
  );
}
